import { Component, OnInit, TemplateRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { WhatsappVoiceCampaignService } from 'src/app/private/services/whatsapp-voice-campaign.service';
import { AiAgentFlowService } from 'src/app/private/services/ai-agent-flow.service';
import { ManageCampaignService } from 'src/app/private/services/manage-campaign.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Router } from '@angular/router';
import { applyCenterScopeToWhere, buildTenantScopeParams, appendCenterScopeToPayload } from 'src/app/utils/tenant-scope.util';
import { filterContactGroupsByCampaignType } from 'src/app/utils/contact-group-type.util';

export interface BatchState {
  index: number;
  contacts: any[];
  status: 'pending' | 'running' | 'done' | 'failed';
  results: any[];
  successCount: number;
  failedCount: number;
}

@Component({
  selector: 'app-whatsapp-voice-campaign',
  templateUrl: './whatsapp-voice-campaign.component.html',
  styleUrls: ['./whatsapp-voice-campaign.component.scss'],
})
export class WhatsappVoiceCampaignComponent implements OnInit {

  // ── List State ──────────────────────────────────────────────────────────────
  campaigns: any[] = [];
  totalRows   = 0;
  currentPage = 0;
  pageSize    = 25;
  loading     = true;

  // ── Form / Create ───────────────────────────────────────────────────────────
  campaignForm!: FormGroup;
  isEditMode = false;
  isViewMode = false;
  editId: number | null = null;
  formLoading = false;
  isSubmitted = false;

  // ── Dropdowns ───────────────────────────────────────────────────────────────
  aiFlows:       any[] = [];
  contactGroups: any[] = [];

  // ── Run / Batch Execution ───────────────────────────────────────────────────
  @Output() runCampaign = new EventEmitter<any>();
  batches: BatchState[] = [];
  allContacts: any[] = [];
  runOverallStatus: 'idle' | 'running' | 'paused' | 'done' | 'failed' = 'idle';
  currentBatchIndex = 0;
  batchCountdownSec = 0;
  countdownTimer: any = null;
  totalSuccess  = 0;
  totalFailed   = 0;
  isRunLoading  = false;

  @ViewChild('formModal')   formModal!:   TemplateRef<any>;
  @ViewChild('runModal')    runModal!:    TemplateRef<any>;
  @ViewChild('deleteModal') deleteModal!: TemplateRef<any>;
  pendingDeleteId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private service: WhatsappVoiceCampaignService,
    private flowService: AiAgentFlowService,
    private campaignApi: ManageCampaignService,
    private modal: NgbModal,
    private toast: ToastrService,
    private ls: LocalStorageService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadCampaigns();
    this.loadFlows();
    this.loadContactGroups();
  }

  initForm(data?: any) {
    this.campaignForm = this.fb.group({
      campaign_name:            [data?.campaign_name       || '', Validators.required],
      contact_group_id:         [data?.contact_group_id    || '', Validators.required],
      ai_agent_flow_id:         [data?.ai_agent_flow_id    || ''],
      call_delay_ms:   [data?.call_delay_ms   ?? 1000, [Validators.required, Validators.min(0)]],
      batch_count:     [data?.batch_count     ?? 1,    [Validators.required, Validators.min(1)]],
      batch_delay_ms:  [data?.batch_delay_ms  ?? 5000, [Validators.required, Validators.min(0)]],
    });
  }

  // ── Computed batch preview ──────────────────────────────────────────────────
  get batchPreview(): string {
    const total = this.selectedGroupContactCount;
    const batches = Number(this.campaignForm.get('batch_count')?.value) || 1;
    const perBatch = Math.ceil(total / batches);
    const batchDelayS = (Number(this.campaignForm.get('batch_delay_ms')?.value) || 0) / 1000;
    const callDelayS  = (Number(this.campaignForm.get('call_delay_ms')?.value)  || 0) / 1000;
    return total > 0
      ? `${total} contacts ÷ ${batches} batches = ~${perBatch} per batch | Call delay: ${callDelayS}s | Batch delay: ${batchDelayS}s`
      : `${batches} batches | Call delay: ${callDelayS}s | Batch delay: ${batchDelayS}s`;
  }

  selectedGroupContactCount = 0;

  onGroupChange(event: any) {
    const gId = this.campaignForm.get('contact_group_id')?.value;
    const group = this.contactGroups.find((g: any) => String(g.id) === String(gId));
    this.selectedGroupContactCount = group?.contact_count || group?.contacts_count || 0;
  }

  // ── Data loading ────────────────────────────────────────────────────────────
  private _isFetchingList = false;

  loadCampaigns() {
    if (this._isFetchingList) return;
    this._isFetchingList = true;
    this.loading = true;
    this.service.list({
      ...buildTenantScopeParams(this.ls),
      page: this.currentPage * this.pageSize,
      limit: this.pageSize,
    }).subscribe({
      next: (res: any) => {
        this.campaigns = res.rows ?? [];
        this.totalRows = res.count ?? 0;
        this.loading   = false;
        this._isFetchingList = false;
      },
      error: () => { 
        this.loading = false; 
        this._isFetchingList = false;
      },
    });
  }

  loadFlows() {
    this.flowService.list({ client_id: this.ls.getClientId(), limit: 100, page: 0 }).subscribe({
      next: (res: any) => { this.aiFlows = res.rows ?? []; },
    });
  }

  loadContactGroups() {
    this.campaignApi.getAllContactGroups({
      where: applyCenterScopeToWhere(
        { client_id: this.ls.getClientId() },
        this.ls
      ),
      order: { col: '"contact_group"."updatedAt"', dir: 'DESC' },
    }).subscribe({
      next: (res: any) => {
        this.contactGroups = filterContactGroupsByCampaignType(res ?? [], 'call');
      },
    });
  }

  editCampaignData: any = null;

  handleFormSubmit() {
    this.modal.dismissAll();
    this.loadCampaigns();
  }

  // ── CRUD ─────────────────────────────────────────────────────────────────────
  openCreate() {
    this.isEditMode  = false;
    this.isViewMode = false;
    this.editId      = null;
    this.editCampaignData = null;
    this.isSubmitted = false;
    this.initForm();
    this.modal.open(this.formModal, { size: 'lg', scrollable: true, centered: true });
  }

  cloningId: number | null = null;

  cloneCampaign(c: any) {
    if (!c || this.cloningId) {
      return;
    }
    this.cloningId = c.id;
    const payload = appendCenterScopeToPayload({
      client_id: this.ls.getClientId(),
      campaign_name: `${c.campaign_name} Copy`,
      contact_group_id: c.contact_group_id,
      ai_agent_flow_id: c.ai_agent_flow_id || null,
      call_delay_ms: c.call_delay_ms ?? 1000,
      batch_count: c.batch_count ?? 1,
      batch_delay_ms: c.batch_delay_ms ?? 5000,
      webhook_url: c.webhook_url,
      webhook_method: c.webhook_method,
      webhook_payload_template: c.webhook_payload_template,
      webhook_headers: c.webhook_headers,
      created_by: this.ls.getUserId(),
      status: 'pending',
    }, this.ls);
    this.service.create(payload).subscribe({
      next: () => {
        this.cloningId = null;
        this.toast.success('Campaign cloned as Pending');
        this.loadCampaigns();
      },
      error: () => {
        this.cloningId = null;
        this.toast.error('Failed to clone campaign');
      }
    });
  }

  openOverview(c: any) {
    this.isViewMode = true;
    this.isEditMode = false;
    this.editId = c.id;
    this.editCampaignData = c;
    this.modal.open(this.formModal, { size: 'lg', scrollable: true, centered: true });
  }

  openEdit(c: any) {
    this.isViewMode = false;
    this.isEditMode  = true;
    this.editId      = c.id;
    this.editCampaignData = c;
    this.isSubmitted = false;
    this.initForm(c);
    this.modal.open(this.formModal, { size: 'lg', scrollable: true, centered: true });
  }

  submitForm() {
    this.isSubmitted = true;
    if (this.campaignForm.invalid) return;
    this.formLoading = true;

    const f = this.campaignForm.value;

    const payload: any = appendCenterScopeToPayload({
      client_id:               this.ls.getClientId(),
      campaign_name:            f.campaign_name,
      contact_group_id:         f.contact_group_id,
      ai_agent_flow_id:         f.ai_agent_flow_id || null,
      call_delay_ms:            Number(f.call_delay_ms),
      batch_count:              Number(f.batch_count),
      batch_delay_ms:           Number(f.batch_delay_ms),
      created_by:               this.ls.getUserId(),
    }, this.ls);

    const call = this.isEditMode
      ? this.service.update({ ...payload, id: this.editId })
      : this.service.create(payload);

    call.subscribe({
      next: () => {
        this.formLoading = false;
        this.modal.dismissAll();
        this.loadCampaigns();
        this.toast.success(`Campaign ${this.isEditMode ? 'updated' : 'created'} successfully`);
      },
      error: (err: any) => {
        this.formLoading = false;
        this.toast.error(err?.error?.message || 'Failed to save campaign');
      },
    });
  }

  openDeleteConfirm(id: number) {
    this.pendingDeleteId = id;
    this.modal.open(this.deleteModal, { size: 'sm', centered: true });
  }

  confirmDelete() {
    if (!this.pendingDeleteId) return;
    this.service.delete({ id: this.pendingDeleteId }).subscribe({
      next: () => {
        this.modal.dismissAll();
        this.loadCampaigns();
        this.toast.success('Campaign deleted');
      },
      error: () => this.toast.error('Delete failed'),
    });
  }

  // ── Run / Batch Execution ───────────────────────────────────────────────────
  openRun(campaign: any) {
    if (campaign.status === 'running') {
      this.toast.warning('Campaign is already running');
      return;
    }
    this.runCampaign.emit(campaign);
  }

  onPageChange(event: any) {
    if (this.currentPage === event.pageIndex && this.pageSize === event.pageSize) {
      return; // Prevent duplicate API call if paginator fires on initialization with same values
    }
    this.currentPage = event.pageIndex;
    this.pageSize    = event.pageSize;
    this.loadCampaigns();
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  get overallProgress(): number {
    const done = this.batches.filter((b) => b.status === 'done').length;
    return this.batches.length > 0 ? Math.round((done / this.batches.length) * 100) : 0;
  }

  viewAnalytics(c: any) {
    this.router.navigate(['main/call-campaign-analytics/' + c.id]);
  }

  ngOnDestroy() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }
}
